import { useState } from 'react';
import { View, Text } from 'react-native';
import { openExternalUrl } from '@/lib/linking';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import {
  ChevronDown,
  ChevronUp,
  Search,
  Mail,
  HelpCircle,
  Send,
  BarChart3,
  FolderOpen,
  Rocket,
  Shield,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { AppLayout } from '@/components/AppLayout';
import { SectionDivider } from '@/components/SectionDivider';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { TextInput } from 'react-native';
import { PLAN_FAQS } from '@/lib/commerce/copy';

const FAQ_DATA = [
  {
    category: 'Your Score',
    icon: BarChart3,
    questions: [
      {
        q: 'How is my Investment Readiness Score calculated?',
        a: 'Your score is calculated across 15 dimensions including team, traction, market size, financials, defensibility, and more. Each criterion is weighted based on its importance to investors at your stage. The score updates automatically when you update your profile or data room.',
      },
      {
        q: 'How can I improve my score?',
        a: 'Check the Score tab for dimension-by-dimension feedback. Each dimension shows what\'s strong and what needs improvement. Common quick wins: completing your team profiles, connecting your data room, and adding traction metrics.',
      },
      {
        q: 'Can I update my profile after creating it?',
        a: 'Yes. Go to your Company Settings to update company info, or the Profile tab for your personal details. Your score recalculates automatically based on new information.',
      },
    ],
  },
  {
    category: 'Data Room',
    icon: FolderOpen,
    questions: [
      {
        q: 'How do I connect my data room?',
        a: 'Go to the Data Room tab and click "Connect Data Room". We support Google Drive and Dropbox integrations, plus direct file uploads. Organize documents by category (pitch deck, financials, legal, team, product, market research).',
      },
      {
        q: 'What documents should I include?',
        a: 'At minimum: your pitch deck, financial projections, and cap table (marked as critical). Important additions: team bios and product roadmap. Recommended: market research and competitive analysis. The Data Room Checklist shows what investors typically expect.',
      },
      {
        q: 'Who can see my data room?',
        a: 'Only investors you explicitly grant access to. You control access per investor and can revoke it at any time. Documents are never shared without your approval.',
      },
    ],
  },
  {
    category: 'Getting Discovered',
    icon: Rocket,
    questions: [
      {
        q: 'How do investors find my startup?',
        a: 'Investors discover startups through the dealflow feed, AI-powered matching based on their investment thesis, and the company directory. A complete profile with a high readiness score increases your visibility.',
      },
      {
        q: 'What are Investor Matches?',
        a: 'Our AI matches your startup with investors based on their stated investment preferences (stage, sector, geography, ticket size). Higher match scores indicate stronger alignment with an investor\'s thesis.',
      },
      {
        q: 'Can I see which investors viewed my profile?',
        a: 'Yes. Your Score screen shows profile views, data room access requests, and engagement metrics over time. You\'ll also receive notifications when an investor requests data room access.',
      },
    ],
  },
  {
    category: 'Account & Subscription',
    icon: Shield,
    questions: [
      {
        q: 'How is my data protected?',
        a: 'Inverio operates under GDPR and Swiss nDSG regulations. Your company data is processed using EU-sovereign AI providers. You can export or delete all your data at any time from Settings > Data & Privacy.',
      },
      ...PLAN_FAQS.startup,
    ],
  },
];

export default function StartupHelpScreen() {
  useTrackScreen('StartupHelp');
  const colors = useThemeColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const toggleQuestion = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const filteredFAQ = searchQuery
    ? FAQ_DATA.map((category) => ({
        ...category,
        questions: category.questions.filter(
          (q) =>
            q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            q.a.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter((category) => category.questions.length > 0)
    : FAQ_DATA;

  return (
    <AppLayout title="Help & Support">
      {/* Was the persona-inconsistent one: (investor)/help.tsx owned its top
         inset and this one owned nothing, so the same destination sat under the
         status bar for founders only. It also opened straight into a search
         field with no visible page title. */}
      <ScreenFrame
        title="Help & Support"
        back={{ fallback: '/(startup)/userprofile', label: 'Profile' }}
      >
        <KeyboardAwareBody className="flex-1" showsVerticalScrollIndicator={false}>
          <View className="px-5 pb-8 pt-1">

            {/* Search */}
            <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
              <View
                className="flex-row items-center rounded-xl px-4"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                <Search size={20} color={colors.text.tertiary} />
                <TextInput
                  style={{ color: colors.text.primary }}
                  className="flex-1 py-3.5 pl-3 text-base"
                  placeholder="Search help topics..."
                  placeholderTextColor={colors.text.tertiary}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                />
              </View>
            </Animated.View>

            {/* Quick Actions */}
            <Animated.View entering={FadeInDown.delay(150).duration(500)} className="mb-6">
              <View className="flex-row gap-3">
                <AnimatedPressable
                  onPress={() => openExternalUrl('mailto:support@inverio.ai', 'StartupHelp.email')}
                  className="flex-1 p-4 rounded-2xl items-center"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <View
                    className="w-11 h-11 rounded-full items-center justify-center mb-2"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Mail size={22} color={colors.primary[600]} />
                  </View>
                  <Text style={{ color: colors.text.primary }} className="font-semibold text-sm">
                    Email Support
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5">
                    support@inverio.ai
                  </Text>
                </AnimatedPressable>

              </View>
            </Animated.View>

            <SectionDivider label="FAQ" marginTop={8} marginBottom={12} />

            {/* FAQ */}
            {filteredFAQ.map((category, catIndex) => {
              const Icon = category.icon;
              return (
                <Animated.View
                  key={category.category}
                  entering={FadeInDown.delay(200 + catIndex * 50).duration(400)}
                  className="mb-4"
                >
                  <View className="flex-row items-center gap-2 mb-2">
                    <Icon size={16} color={colors.primary[600]} />
                    <Text style={{ color: colors.text.primary }} className="font-semibold">
                      {category.category}
                    </Text>
                  </View>
                  <View
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                    }}
                  >
                    {category.questions.map((faq, qIndex) => {
                      const questionId = `${catIndex}-${qIndex}`;
                      const isExpanded = expandedQuestions.has(questionId);

                      return (
                        <View key={questionId}>
                          <AnimatedPressable
                            onPress={() => toggleQuestion(questionId)}
                            className="flex-row items-center px-4 py-3.5"
                            style={{
                              borderBottomWidth: qIndex < category.questions.length - 1 || isExpanded ? 1 : 0,
                              borderColor: colors.border.light,
                            }}
                          >
                            <Text style={{ color: colors.text.primary }} className="flex-1 font-medium text-sm">
                              {faq.q}
                            </Text>
                            {isExpanded ? (
                              <ChevronUp size={18} color={colors.text.tertiary} />
                            ) : (
                              <ChevronDown size={18} color={colors.text.tertiary} />
                            )}
                          </AnimatedPressable>
                          {isExpanded && (
                            <View
                              className="px-4 py-3"
                              style={{ backgroundColor: colors.surface.secondary }}
                            >
                              <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                                {faq.a}
                              </Text>
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>
                </Animated.View>
              );
            })}

            {filteredFAQ.length === 0 && (
              <View className="items-center py-8">
                <HelpCircle size={32} color={colors.text.tertiary} />
                <Text style={{ color: colors.text.tertiary }} className="mt-2">
                  No results for &quot;{searchQuery}&quot;
                </Text>
              </View>
            )}

            {/* Still need help */}
            <Animated.View entering={FadeInUp.delay(400).duration(500)} className="mt-4">
              <View
                className="p-5 rounded-2xl"
                style={{
                  backgroundColor: colors.primary[50],
                  borderWidth: 1,
                  borderColor: colors.primary[200],
                }}
              >
                <Text style={{ color: colors.primary[700] }} className="text-base font-semibold mb-1">
                  Still need help?
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-sm mb-4">
                  Our support team typically responds within 24 hours.
                </Text>
                <AnimatedPressable
                  onPress={() => openExternalUrl('mailto:support@inverio.ai?subject=Startup%20Support%20Request', 'StartupHelp.email')}
                  className="flex-row items-center justify-center gap-2 py-3 rounded-xl"
                  style={{ backgroundColor: colors.primary[600] }}
                >
                  <Send size={16} color="white" />
                  <Text className="text-white font-semibold text-sm">Send a Message</Text>
                </AnimatedPressable>
              </View>
            </Animated.View>

          </View>
        </KeyboardAwareBody>
      </ScreenFrame>
    </AppLayout>
  );
}
